import { createAsyncThunk } from "@reduxjs/toolkit";
import { permissionsApi } from "../../api/permissionsApi";
import { Permission, PermissionEntityType } from "../../types/permissionType";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchAllPermissionsThunk = createAsyncThunk<
    Permission[],
    void,
    { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
    "permissions/fetchAllDefinitions",
    async (_, { dispatch, rejectWithValue }) => {
        const res = await handleRequest<Permission[]>(
            dispatch,
            "indirect",
            () => permissionsApi.getAllPermissions()
        );
        if (!res.success()) {
            return rejectWithValue(res.errorMessage ?? "Failed to fetch permissions");
        }
        return res.result as Permission[];
    }
);

export const fetchUserPermissionsThunk = createAsyncThunk<
    { userId: string; permissions: Permission[] },
    string,
    { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
    "permissions/fetchUserPermissions",
    async (userId, { dispatch, rejectWithValue }) => {
        const res = await handleRequest<Permission[]>(
            dispatch,
            "indirect",
            () => permissionsApi.getSingleUserPermission(userId)
        );
        if (!res.success()) {
            return rejectWithValue(res.errorMessage ?? "Failed to fetch user permissions");
        }
        return { userId, permissions: res.result as Permission[] };
    }
);

export const addGeneralPermissionThunk = createAsyncThunk<
    void,
    {
        userId: string;
        entityType: PermissionEntityType;
        permissionName: string;
    },
    { dispatch: AppDispatch; state: RootState }
>(
    "permissions/addGeneralPermission",
    async ({ userId, entityType, permissionName }, { dispatch }) => {
        const res = await handleRequest<void>(
            dispatch,
            "direct",
            () => permissionsApi.addGeneralPermission(userId, entityType, permissionName),
            i18n.t("requestMessage.permissionAdded")
        );
        if (res.success()) {
            await dispatch(fetchUserPermissionsThunk(userId));
        }
    }
);

export const removeGeneralPermissionThunk = createAsyncThunk<
    void,
    {
        userId: string;
        entityType: PermissionEntityType;
        permissionName: string;
    },
    { dispatch: AppDispatch; state: RootState }
>(
    "permissions/removeGeneralPermission",
    async ({ userId, entityType, permissionName }, { dispatch }) => {
        const res = await handleRequest<void>(
            dispatch,
            "direct",
            () =>
                permissionsApi.removeGeneralPermission(userId, entityType, permissionName),
            i18n.t("requestMessage.permissionRemoved")
        );
        if (res.success()) {
            await dispatch(fetchUserPermissionsThunk(userId));
        }
    }
);

export const addEntityPermissionThunk = createAsyncThunk<
    void,
    {
        userId: string;
        entityType: PermissionEntityType;
        permissionName: string;
        targetEntityId: number;
    },
    { dispatch: AppDispatch; state: RootState }
>(
    "permissions/addEntityPermission",
    async ({ userId, entityType, permissionName, targetEntityId }, { dispatch }) => {
        const res = await handleRequest<void>(
            dispatch,
            "direct",
            () =>
                permissionsApi.addEntityPermission(
                    userId,
                    entityType,
                    permissionName,
                    targetEntityId
                ),
            i18n.t("requestMessage.permissionAdded")
        );
        if (res.success()) {
            await dispatch(fetchUserPermissionsThunk(userId));
        }
    }
);

export const removeEntityPermissionThunk = createAsyncThunk<
    void,
    {
        userId: string;
        entityType: PermissionEntityType;
        permissionName: string;
        targetEntityId: number;
    },
    { dispatch: AppDispatch; state: RootState }
>(
    "permissions/removeEntityPermission",
    async (
        { userId, entityType, permissionName, targetEntityId },
        { dispatch }
    ) => {
        const res = await handleRequest<void>(
            dispatch,
            "direct",
            () =>
                permissionsApi.removeEntityPermission(
                    userId,
                    entityType,
                    permissionName,
                    targetEntityId
                ),
            i18n.t("requestMessage.permissionRemoved")
        );
        if (res.success()) {
            await dispatch(fetchUserPermissionsThunk(userId));
        }
    }
);
